import { useState, useRef, useCallback, useMemo } from "react";
import { Form, Input, Button, Checkbox, Divider, App, Typography } from "antd";
import { useGoogleLogin } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { FaRegCircleUser } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { AiOutlineGoogle } from "react-icons/ai";
import store from "store";

import { getSavedEmail } from "utils/cookies";
import { loginModalSelector } from "redux/selectors";
import { hideLoginModal } from "redux/loginSlice";
import { logout } from "redux/configSlice";

const { Text } = Typography;

const LoginForm = ({ onSubmit, onGoogleLogin, loading, email }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const emailRef = useRef();
  const passwordRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginModal = useSelector(loginModalSelector);
  const [form] = Form.useForm();
  const watchEmail = Form.useWatch("email", form);

  const [editEmail, setEditEmail] = useState(false);

  const savedEmail = email || getSavedEmail();
  const failureGoogleLoginHandler = useCallback(
    (e) => {
      message.error(e?.message || t("notification.login_google_failure"));
    },
    [t, message]
  );
  const login = useGoogleLogin({
    onSuccess: onGoogleLogin,
    onError: failureGoogleLoginHandler,
    flow: "auth-code",
  });

  const googleLoginHandler = useCallback(() => login(), [login]);

  const ssoLoginHandler = () => {
    navigate("/user/login/sso");
  };

  const goToForgotPassword = useCallback(() => {
    if (loginModal?.show) {
      store.remove("app.endpoint");
      dispatch(logout());
      dispatch(hideLoginModal());
      navigate("/user/forgot", { redirect: true });
    }
    navigate("/user/forgot");
  }, [navigate, dispatch, loginModal]);

  const editHandler = useCallback(() => {
    setEditEmail(true);
    setTimeout(() => {
      if (emailRef?.current) {
        if (typeof emailRef?.current?.focus === "function") {
          emailRef?.current?.focus();
        }
        if (
          savedEmail &&
          !editEmail &&
          typeof emailRef?.current?.input?.setSelectionRange === "function"
        ) {
          emailRef?.current?.input?.setSelectionRange(
            0,
            form.getFieldValue("email").length
          );
        }
      }
    }, 1);
  }, [form, savedEmail, editEmail]);

  const initialValues = useMemo(
    () => ({ email: savedEmail, remember: true }),
    [savedEmail]
  );

  return (
    <Form
      form={form}
      name="basic"
      layout="vertical"
      requiredMark={false}
      initialValues={initialValues}
      onFinish={onSubmit}
      autoComplete="off"
      size="small"
    >
      <Form.Item label={t("users.email")}>
        <div>
          {savedEmail && !editEmail ? (
            <div>
              {watchEmail || savedEmail}
              <Button
                icon={<EditOutlined />}
                shape="circle"
                type="text"
                onClick={editHandler}
              />
            </div>
          ) : null}
          <Form.Item
            name="email"
            rules={[
              {
                required: true,
                message: t("users.please_input_your_email_address"),
              },
              {
                type: "email",
                message: t("users.email_is_not_a_valid_email"),
              },
            ]}
            noStyle
          >
            <Input
              ref={emailRef}
              size="middle"
              type={savedEmail && !editEmail ? "hidden" : undefined}
              autoFocus
              onBlur={() => setEditEmail(false)}
            />
          </Form.Item>
        </div>
      </Form.Item>
      <Form.Item
        label={t("users.password")}
        name="password"
        rules={[
          { required: true, message: t("users.please_input_your_password") },
        ]}
      >
        <Input.Password autoFocus ref={passwordRef} size="middle" />
      </Form.Item>

      <div className="flex items-center justify-between">
        <Form.Item name="remember" valuePropName="checked">
          <Checkbox>{t("users.remember_me")}</Checkbox>
        </Form.Item>
        <Form.Item>
          <Text
            className="text-link-primary hover:cursor-pointer"
            onClick={goToForgotPassword}
          >
            {t("users.forgot_password_")}
          </Text>
        </Form.Item>
      </div>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          loading={loading}
          disabled={!!loading}
          size="middle"
        >
          {t("users.login")}
        </Button>
        <Divider />
        <div className="flex flex-col gap-3">
          <Button
            type="default"
            variant="outlined"
            icon={
              loading ? (
                <AiOutlineGoogle className="text-xl" />
              ) : (
                <FcGoogle className="text-xl" />
              )
            }
            onClick={googleLoginHandler}
            className="h-[32px]"
            disabled={loading}
            block
          >
            {t("users.login_with_google")}
          </Button>
          <Button
            type="default"
            variant="outlined"
            icon={<FaRegCircleUser className="text-lg" />}
            onClick={ssoLoginHandler}
            className="h-[32px]"
            disabled={loading}
            block
          >
            {t("sso.login_with_sso")}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default LoginForm;
